import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import FolderTree from './FolderTree.jsx';
import FolderGrid from './FolderGrid.jsx';
import './FolderExplorer.css';

const api = {
  tree: '/api/v1/folders/tree',
  create: '/api/v1/folders',
  update: id => `/api/v1/folders/${id}`,
  move: id => `/api/v1/folders/${id}/move`,
  moveItems: '/api/v1/folders/move-items',
  attachItems: id => `/api/v1/folders/${id}/attach-items`
};

export default function FolderExplorer() {
  const [rawTree, setRawTree] = useState([]);          // nested server data
  const [flatItems, setFlatItems] = useState([]);      // flattened for tree hook
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);  // local selection (single)
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameState, setRenameState] = useState({ id:null, title:'', description:'' });
  const [cutBuffer, setCutBuffer] = useState([]);
  const [items, setItems] = useState([]);              // placeholder for folder contents
  const [loading, setLoading] = useState(false);
  const [error,setError] = useState('');

  // Flatten nested nodes
  const flatten = useCallback((nodes, parentId = null, acc = []) => {
    nodes.forEach(n => {
      acc.push({
        id: String(n.id),
        parentId: parentId ? String(parentId) : null,
        data: { title: n.title, description: n.description || '', tid: n.tid || '' }
      });
      if (Array.isArray(n.children) && n.children.length) {
        flatten(n.children, n.id, acc);
      }
    });
    return acc;
  }, []);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(api.tree);
      if (data.success) {
        setRawTree(data.data || []);
        const flat = flatten(data.data || []);
        setFlatItems(flat);
        if (activeFolderId && !flat.find(i => i.id === String(activeFolderId))) {
          setActiveFolderId(null);
          setSelectedIds([]);
        }
      }
    } catch {
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [activeFolderId, flatten]);

  useEffect(() => { loadTree(); }, [loadTree]);

  const rootItems = useMemo(
    () => flatItems.filter(i => i.parentId === null)
                   .sort((a,b)=> (a.data.title||'').localeCompare(b.data.title||'')),
    [flatItems]
  );

  const getChildren = useCallback(
    id => flatItems
      .filter(i => i.parentId === id)
      .sort((a,b)=> (a.data.title||'').localeCompare(b.data.title||'')),
    [flatItems]
  );

  // MOVED: declare moveFolder before treeProp to avoid TDZ/initialization error
  const moveFolder = useCallback(async (dragId, targetId) => {
    if (dragId === targetId) return;
    try {
      await axios.post(api.move(dragId), { new_parent_id: targetId || null });
      loadTree();
    } catch {
      setError('Move failed');
    }
  }, [loadTree]);

  // NEW: synchronous tree object passed to FolderTree so external code can call
  // retrieveChildrenIds/getItem immediately (prevents retrieveChildrenIds is not a function)
  const treeProp = useMemo(() => {
    const getItem = (id) => flatItems.find(i => String(i.id) === String(id)) || null;
    const retrieveChildrenIds = (parentId) => getChildren(parentId).map(i => String(i.id));
    return {
      items: flatItems,
      rootItems,
      getChildren,
      moveFolder,
      getItem,
      retrieveChildrenIds
    };
  }, [flatItems, rootItems, getChildren, moveFolder]);

  const refreshItemsForFolder = useCallback(folderId => {
    // Placeholder: later fetch items (pages etc) for folderId
    setItems([]);
  }, []);

  const selectFolder = id => {
    setActiveFolderId(id);
    setSelectedIds([id]);
    refreshItemsForFolder(id);
  };

  const onCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(api.create, { title:newFolderName.trim(), parent_id:activeFolderId });
      setNewFolderName('');
      setCreating(false);
      loadTree();
    } catch {
      setError('Create failed');
    }
  };

  const beginRename = item => {
    setRenameState({ id:item.id, title:item.data.title, description:item.data.description });
  };

  const commitRename = async () => {
    if (!renameState.id || !renameState.title.trim()) return;
    try {
      await axios.put(api.update(renameState.id), {
        title: renameState.title.trim(),
        description: renameState.description
      });
      // Optimistic local update
      setFlatItems(prev => prev.map(it =>
        it.id === renameState.id
          ? { ...it, data:{ ...it.data, title: renameState.title.trim(), description: renameState.description } }
          : it
      ));
      setRenameState({ id:null, title:'', description:'' });
    } catch {
      setError('Rename failed');
    }
  };

  const cancelRename = () => setRenameState({ id:null, title:'', description:'' });

  const cutSelected = () => {
    if (!selectedIds.length) return;
    setCutBuffer(selectedIds.map(i => parseInt(i,10)));
    setSelectedIds([]);
  };

  const pasteToFolder = async () => {
    if (!cutBuffer.length || !activeFolderId) return;
    try {
      await axios.post(api.moveItems, {
        target_folder_id: activeFolderId,
        item_ids: cutBuffer
      });
      setCutBuffer([]);
      refreshItemsForFolder(activeFolderId);
    } catch {
      setError('Paste failed');
    }
  };

  const attachItems = async itemIds => {
    if (!activeFolderId || !itemIds.length) return;
    try {
      await axios.post(api.attachItems(activeFolderId), { item_ids:itemIds });
      refreshItemsForFolder(activeFolderId);
    } catch {
      setError('Attach failed');
    }
  };

  return (
    <div className="folder-explorer">
      <div className="fe-sidebar">
        <div className="fe-header">
          <h5>Folders</h5>
          <button
            className="btn btn-xs btn-primary"
            onClick={()=>setCreating(v=>!v)}
          >{creating?'Cancel':'New'}</button>
        </div>
        {creating && (
          <div className="fe-new-folder">
            <input
              className="form-control form-control-sm"
              placeholder="Folder name"
              value={newFolderName}
              onChange={e=>setNewFolderName(e.target.value)}
              onKeyDown={e=>e.key==='Enter' && onCreateFolder()}
            />
            <button
              className="mt-1 btn btn-xs btn-success"
              onClick={onCreateFolder}
            >Save</button>
          </div>
        )}

        <FolderTree
          tree={treeProp}
          activeId={activeFolderId}
          onSelect={selectFolder}
          onRename={beginRename}
          renameState={renameState}
          setRenameState={setRenameState}
          commitRename={commitRename}
          cancelRename={cancelRename}
          selectionIds={selectedIds}
          setSelectionIds={setSelectedIds}
        />
      </div>

      <div className="fe-main">
        <div className="fe-toolbar">
          <div><strong>Folder:</strong> {activeFolderId ?? '[Root]'}</div>
          <div className="fe-actions">
            <button
              className="btn btn-xs btn-secondary"
              disabled={!selectedIds.length}
              onClick={cutSelected}
            >Cut</button>
            <button
              className="btn btn-xs btn-secondary"
              disabled={!cutBuffer.length || !activeFolderId}
              onClick={pasteToFolder}
            >Paste</button>
          </div>
        </div>
        <FolderGrid
          items={items}
          selected={new Set()} // Placeholder (grid selection not yet integrated)
          onSelect={()=>{}}
          onAttachDropped={attachItems}
        />
      </div>

      {loading && <div className="fe-loading-overlay">Loading...</div>}
      {error && <div className="alert alert-danger fe-error">{error}</div>}
    </div>
  );
}

